import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import {
  FaChevronLeft,
  FaChevronRight,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaCheckCircle,
} from "react-icons/fa";
import axios from "axios";
import ApiUrl from "../Environment/ApiUrl";

const Report = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("sales");
  const [salesData, setSalesData] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [dealerData, setDealerData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [profitData, setProfitData] = useState([]);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Fetch data based on category
  const fetchSales = async () => {
    if (selectedCategory !== "sales") return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${ApiUrl}/sales_data.php`, {
        params: {
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
      });
      if (response.data && response.data.data) {
        setSalesData(response.data.data);
      } else {
        setSalesData([]);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
      setError("Failed to load sales data");
      setSalesData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPurchases = async () => {
    if (selectedCategory !== "purchase") return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${ApiUrl}/purchase_data.php`, {
        params: {
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
      });
      if (response.data && response.data.data) {
        setPurchaseData(response.data.data);
      } else {
        setPurchaseData([]);
      }
    } catch (error) {
      console.error("Error fetching purchases:", error);
      setError("Failed to load purchase data");
      setPurchaseData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStocks = async () => {
    if (selectedCategory !== "stocks") return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${ApiUrl}/stock_data.php`, {
        params: {
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
      });
      if (response.data && response.data.data) {
        setStockData(response.data.data);
      } else {
        setStockData([]);
        setError("No stock data returned from API");
      }
    } catch (error) {
      console.error("Error fetching stocks:", error);
      setError("Failed to load stock data");
      setStockData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDealers = async () => {
    if (selectedCategory !== "dealer") return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${ApiUrl}/dealer_data.php`);
      if (response.data && response.data.data) {
        setDealerData(response.data.data);
      } else {
        setDealerData([]);
      }
    } catch (error) {
      console.error("Error fetching dealers:", error);
      setError("Failed to load dealer data");
      setDealerData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomers = async () => {
    if (selectedCategory !== "customer") return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${ApiUrl}/customer_data.php`);
      if (response.data && response.data.data) {
        setCustomerData(response.data.data);
      } else {
        setCustomerData([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setError("Failed to load customer data");
      setCustomerData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfitReport = async () => {
    if (selectedCategory !== "profit_report") return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${ApiUrl}/profit_report.php`, {
        params: {
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
      });
      if (response.data && response.data.data) {
        setProfitData(response.data.data);
      } else {
        setProfitData([]);
        setError("No profit data returned from API");
      }
    } catch (error) {
      console.error("Error fetching profit report:", error);
      setError("Failed to load profit report data");
      setProfitData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Export functions
  const exportToExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, filename);
  };

  const exportToPDF = (data, title, headers, bodyMap) => {
    const doc = new jsPDF();
    doc.text(title, 14, 10);
    autoTable(doc, {
      startY: 20,
      head: [headers],
      body: data.map(bodyMap),
    });
    doc.save(`${title.toLowerCase().replace(" ", "_")}.pdf`);
  };

  const handlePrintAllPages = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
            <html>
                <head>
                    <title>Print ${
                      selectedCategory.charAt(0).toUpperCase() +
                      selectedCategory.slice(1)
                    } Report</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        table { width: 100%; border-collapse: collapse; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #d3d3d3; font-weight: bold; }
                        .payment-status { display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; border-radius: 12px; }
                        .bg-success { background-color: #d4edda; }
                        .bg-warning { background-color: #fff3cd; }
                        .bg-danger { background-color: #f8d7da; }
                        .text-success { color: #28a745; }
                        .text-warning { color: #ffc107; }
                        .text-danger { color: #dc3545; }
                    </style>
                </head>
                <body>
                    <h2>${
                      selectedCategory.charAt(0).toUpperCase() +
                      selectedCategory.slice(1)
                    } Report</h2>
                    <table>
                        <thead>
                            ${
                              selectedCategory === "sales"
                                ? `
                                <tr>
                                    <th>S.No</th>
                                    <th>Invoice No</th>
                                    <th>Purchase Date</th>
                                    <th>Total Amount</th>
                                    <th>Paid Amount</th>
                                
                                </tr>
                            `
                                : selectedCategory === "purchase"
                                ? `
                                <tr>
                                    <th>S.No</th>
                                    <th>Purchase No</th>
                                    <th>Date</th>
                                    <th>Name</th>
                                    <th>Total Amount</th>
                                    <th>Paid Amount</th>
                                    <th>Quantity</th>
                                    <th>Payment Status</th>
                                </tr>
                            `
                                : selectedCategory === "stocks"
                                ? `
                                <tr>
                                    <th>S.No</th>
                                    <th>Item Code</th>
                                    <th>Item Name</th>
                                    <th>Brand Name</th>
                                    <th>Model Name</th>
                                    <th>Type</th>
                                    <th>Tyre Size</th>
                                    <th>Stock Qty</th>
                                    <th>Available Qty</th>
                                </tr>
                            `
                                : selectedCategory === "dealer"
                                ? `
                                <tr>
                                    <th>S.No</th>
                                    <th>Dealer ID</th>
                                    <th>Company Name</th>
                                    <th>Dealer Name</th>
                                    <th>Mobile Number</th>
                                    <th>WhatsApp Number</th>
                                    <th>GST Number</th>
                                    <th>Address</th>
                                    <th>Status</th>
                                </tr>
                            `
                                : selectedCategory === "customer"
                                ? `
                                <tr>
                                    <th>S.No</th>
                                    <th>Customer ID</th>
                                    <th>Name</th>
                                    <th>Customer Type</th>
                                    <th>Branch Name</th>
                                    <th>Mobile Number</th>
                                    <th>Address</th>
                                    <th>Status</th>
                                </tr>
                            `
                                : selectedCategory === "profit_report"
                                ? `
                                <tr>
                                    <th>S.No</th>
                                    <th>Product Category</th>
                                    <th>Type</th>
                                    <th>Size</th>
                                    <th>Purchase Rate</th>
                                    <th>Mrp</th>
                                    <th>Profit Value</th>
                                    <th>Percentage</th>
                                </tr>
                            `
                                : ""
                            }
                        </thead>
                        <tbody>
                            ${dataToShow
                              .map(
                                (item, index) => `
                                <tr>
                                    ${
                                      selectedCategory === "sales"
                                        ? `
                                        <td>${index + 1}</td>
                                        <td>${item.Sales_ID}</td>
                                        <td>${item.Purchase_Date}</td>
                                        <td>${formatCurrency(
                                          item.Net_total
                                        )}</td>
                                        <td>${formatCurrency(
                                          item.Paid_Amount
                                        )}</td>
                                     
                                    `
                                        : selectedCategory === "purchase"
                                        ? `
                                        <td>${index + 1}</td>
                                        <td>${item.purchaseId}</td>
                                        <td>${item.purchaseDate}</td>
                                        <td>${item.dealerName}</td>
                                        <td>${item.purchaseType}</td>
                                        <td>${formatCurrency(
                                          item.netTotal
                                        )}</td>
                                        <td>${formatCurrency(
                                          item.paidAmount
                                        )}</td>
                                        <td>${
                                          item.purchaseItems?.[0]
                                            ?.purchase_quantity || "0"
                                        }</td>
                                        <td><span class="payment-status ${getPaymentStatusClass(
                                          item.paymentStatus
                                        )}">${item.paymentStatus}</span></td>
                                    `
                                        : selectedCategory === "stocks"
                                        ? `
                                        <td>${index + 1}</td>
                                        <td>${item.item_code || "-"}</td>
                                        <td>${item.item_name || "-"}</td>
                                        <td>${item.brand_name || "-"}</td>
                                        <td>${item.model_name || "-"}</td>
                                        <td>${item.type || "-"}</td>
                                        <td>${item.tyre_size || "-"}</td>
                                        <td>${item.stock_quantity || "0"}</td>
                                        <td>${
                                          item.available_quantity || "0"
                                        }</td>
                                    `
                                        : selectedCategory === "dealer"
                                        ? `
                                        <td>${index + 1}</td>
                                        <td>${item.dealer_id}</td>
                                        <td>${item.company_name}</td>
                                        <td>${item.dealer_name}</td>
                                        <td>${item.mobile_number}</td>
                                        <td>${item.whatsapp_number}</td>
                                        <td>${item.gst_number}</td>
                                        <td>${item.address}</td>
                                        <td><span class="payment-status ${getPaymentStatusClass(
                                          item.status
                                        )}">${item.status}</span></td>
                                    `
                                        : selectedCategory === "customer"
                                        ? `
                                        <td>${index + 1}</td>
                                        <td>${item.customer_id}</td>
                                        <td>${item.name}</td>
                                        <td>${item.customer_type}</td>
                                        <td>${item.branch_name}</td>
                                        <td>${item.mobile_number}</td>
                                        <td>${item.address}</td>
                                        <td><span class="payment-status ${getPaymentStatusClass(
                                          item.status
                                        )}">${item.status}</span></td>
                                    `
                                        : selectedCategory === "profit_report"
                                        ? `
                                        <td>${index + 1}</td>
                                        <td>${item.product_category}</td>
                                        <td>${item.type}</td>
                                        <td>${item.size}</td>
                                        <td>${item.purchase_rate}</td>
                                        <td>${item.mrp}</td>
                                        <td>${item.profit_value}</td>
                                        <td>${item.percentage}</td>
                                    `
                                        : ""
                                    }
                                </tr>
                            `
                              )
                              .join("")}
                        </tbody>
                    </table>
                </body>
            </html>
        `);
    printWindow.document.close();
    printWindow.print();
  };

  const getPaymentStatusClass = (status) => {
    const normalized = status?.toLowerCase();
    const statusMap = {
      paid: "bg-success text-success",
      partial: "bg-warning text-warning",
      unpaid: "bg-danger text-danger",
      active: "bg-success text-success",
      inactive: "bg-danger text-danger",
    };
    return statusMap[normalized] || "bg-secondary text-secondary";
  };

  const handlePrint = () => {
    const printContents = document.getElementById("report-table").outerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const getPaymentStatusBadge = (status) => {
    const normalized = status?.toLowerCase();
    const statusMap = {
      paid: "bg-success bg-opacity-10 text-success",
      partial: "bg-warning bg-opacity-10 text-warning",
      unpaid: "bg-danger bg-opacity-10 text-danger",
      active: "bg-success bg-opacity-10 text-success",
      inactive: "bg-danger bg-opacity-10 text-danger",
    };
    const iconMap = {
      paid: <FaCheckCircle size={14} />,
      partial: "⚠",
      unpaid: "×",
      active: "✓",
      inactive: "×",
    };
    return (
      <span
        className={`d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill small fw-medium ${
          statusMap[normalized] || "bg-secondary bg-opacity-10 text-secondary"
        }`}
      >
        {iconMap[normalized] || "?"} {status}
      </span>
    );
  };

  const isDateInRange = (date, from, to) => {
    if (!from && !to) return true;
    const dateObj = new Date(date);
    const fromDateObj = from ? new Date(from) : null;
    const toDateObj = to ? new Date(to) : null;
    if (fromDateObj) fromDateObj.setHours(0, 0, 0, 0);
    if (toDateObj) toDateObj.setHours(23, 59, 59, 999);
    return (
      (!fromDateObj || dateObj >= fromDateObj) &&
      (!toDateObj || dateObj <= toDateObj)
    );
  };

  const filteredStocks = stockData.filter(
    (stock) =>
      Object.values(stock)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) &&
      isDateInRange(stock.date_added, fromDate, toDate)
  );

  const filteredSales = salesData.filter(
    (sale) =>
      Object.values(sale)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) &&
      isDateInRange(sale.Purchase_Date, fromDate, toDate)
  );

  const filteredPurchases = purchaseData.filter(
    (purchase) =>
      Object.values(purchase)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) &&
      isDateInRange(purchase.purchaseDate, fromDate, toDate)
  );

  const filteredDealers = dealerData.filter((dealer) =>
    Object.values(dealer)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const filteredCustomers = customerData.filter((customer) =>
    Object.values(customer)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const filteredProfitReport = profitData.filter((profit) =>
    Object.values(profit)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const dataToShow =
    selectedCategory === "sales"
      ? filteredSales
      : selectedCategory === "purchase"
      ? filteredPurchases
      : selectedCategory === "stocks"
      ? filteredStocks
      : selectedCategory === "dealer"
      ? filteredDealers
      : selectedCategory === "customer"
      ? filteredCustomers
      : selectedCategory === "profit_report"
      ? filteredProfitReport
      : [];

  const totalPages = Math.ceil(dataToShow.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = dataToShow.slice(startIndex, startIndex + itemsPerPage);

  const handleFilter = () => {
    if (selectedCategory === "sales") {
      fetchSales();
    } else if (selectedCategory === "purchase") {
      fetchPurchases();
    } else if (selectedCategory === "stocks") {
      fetchStocks();
    } else if (selectedCategory === "dealer") {
      fetchDealers();
    } else if (selectedCategory === "customer") {
      fetchCustomers();
    } else if (selectedCategory === "profit_report") {
      fetchProfitReport();
    } else {
      setCurrentPage(1);
    }
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setSearchTerm("");
    setCurrentPage(1);
    setSelectedCategory("");
    setSalesData([]);
    setPurchaseData([]);
    setStockData([]);
    setDealerData([]);
    setCustomerData([]);
    setProfitData([]);
    setError(null);
  };

  useEffect(() => {
    if (selectedCategory === "sales") {
      fetchSales();
    } else if (selectedCategory === "purchase") {
      fetchPurchases();
    } else if (selectedCategory === "stocks") {
      fetchStocks();
    } else if (selectedCategory === "dealer") {
      fetchDealers();
    } else if (selectedCategory === "customer") {
      fetchCustomers();
    } else if (selectedCategory === "profit_report") {
      fetchProfitReport();
    }
  }, [selectedCategory]);

  const formatCurrency = (amount) => {
    return `₹${
      Number(amount.replace(/[^0-9.-]+/g, "")).toLocaleString("en-IN") || "0"
    }`;
  };

  return (
    <div className="bg-white font-sans">
      {/* Header */}
      <div className="p-3 p-md-4 bg-white">
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ flexWrap: "wrap" }}
        >
          <h2 className="m-0 fs-5 fw-semibold text-dark">Report</h2>
          <div className="d-flex gap-2 mt-2" style={{ flexWrap: "wrap" }}>
            {selectedCategory && (
              <button
                onClick={handlePrintAllPages}
                className="btn btn-sm btn-danger d-flex align-items-center gap-2"
                disabled={isLoading}
              >
                <FaPrint size={14} />
                Print
              </button>
            )}
            {selectedCategory === "stocks" && (
              <>
                <button
                  onClick={handlePrint}
                  className="btn btn-sm btn-danger d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaPrint size={14} />
                  Print Current Page
                </button>
                <button
                  onClick={() =>
                    exportToExcel(filteredStocks, "stocks_report.xlsx")
                  }
                  className="btn btn-sm btn-success d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFileExcel size={14} />
                  Excel
                </button>
                <CSVLink
                  data={filteredStocks}
                  filename="stocks_report.csv"
                  className="btn btn-sm btn-warning d-flex align-items-center gap-2"
                >
                  <FaFilePdf size={14} />
                  CSV
                </CSVLink>
                <button
                  onClick={() =>
                    exportToPDF(
                      filteredStocks,
                      "Stock Report",
                      [
                        "Item Code",
                        "Item Name",
                        "Brand Name",
                        "Model Name",
                        "Type",
                        "Tyre Size",
                        "Stock Qty",
                        "Available Qty",
                      ],
                      (s) => [
                        s.item_code || "-",
                        s.item_name || "-",
                        s.brand_name || "-",
                        s.model_name || "-",
                        s.type || "-",
                        s.tyre_size || "-",
                        s.stock_quantity || "0",
                        s.available_quantity || "0",
                      ]
                    )
                  }
                  className="btn btn-secondary d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFilePdf size={14} />
                  PDF
                </button>
              </>
            )}
            {selectedCategory === "sales" && (
              <>
                <button
                  onClick={() =>
                    exportToExcel(filteredSales, "sales_report.xlsx")
                  }
                  className="btn btn-success d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFileExcel size={14} />
                  Excel
                </button>
                <CSVLink
                  data={filteredSales}
                  filename="sales_report.csv"
                  className="btn btn-warning d-flex align-items-center gap-2"
                >
                  <FaFilePdf size={14} />
                  CSV
                </CSVLink>
                <button
                  onClick={() =>
                    exportToPDF(
                      filteredSales,
                      "Sales Report",
                      [
                        "Sales ID",
                        "Purchase Date",

                        "Total Amount",
                        "Paid Amount",
                      ],
                      (s) => [
                        s.Sales_ID,
                        s.Purchase_Date,

                        formatCurrency(s.Net_total),
                        formatCurrency(s.Paid_Amount),
                      ]
                    )
                  }
                  className="btn btn-secondary d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFilePdf size={14} />
                  PDF
                </button>
              </>
            )}
            {selectedCategory === "purchase" && (
              <>
                <button
                  onClick={() =>
                    exportToExcel(filteredPurchases, "purchase_report.xlsx")
                  }
                  className="btn btn-success d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFileExcel size={14} />
                  Excel
                </button>
                <CSVLink
                  data={filteredPurchases}
                  filename="purchase_report.csv"
                  className="btn btn-warning d-flex align-items-center gap-2"
                >
                  <FaFilePdf size={14} />
                  CSV
                </CSVLink>
                <button
                  onClick={() =>
                    exportToPDF(
                      filteredPurchases,
                      "Purchase Report",
                      [
                        "Purchase No",
                        "Date",
                        "Name",
                        "Total Amount",
                        "Paid Amount",
                        "Quantity",
                        "Payment Status",
                      ],
                      (p) => [
                        p.purchaseId,
                        p.purchaseDate,
                        p.dealerName,
                        formatCurrency(p.netTotal),
                        formatCurrency(p.paidAmount),
                        p.purchaseItems?.[0]?.purchase_quantity || "0",
                        p.paymentStatus,
                      ]
                    )
                  }
                  className="btn btn-secondary d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFilePdf size={14} />
                  PDF
                </button>
              </>
            )}
            {selectedCategory === "dealer" && (
              <>
                <button
                  onClick={() =>
                    exportToExcel(filteredDealers, "dealer_report.xlsx")
                  }
                  className="btn btn-success d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFileExcel size={14} />
                  Excel
                </button>
                <CSVLink
                  data={filteredDealers}
                  filename="dealer_report.csv"
                  className="btn btn-warning d-flex align-items-center gap-2"
                >
                  <FaFilePdf size={14} />
                  CSV
                </CSVLink>
                <button
                  onClick={() =>
                    exportToPDF(
                      filteredDealers,
                      "Dealer Report",
                      [
                        "Dealer ID",
                        "Company Name",

                        "Mobile Number",
                        "GST Number",
                        "Address",
                        "Status",
                      ],
                      (d) => [
                        d.dealer_id,
                        d.company_name,
                        d.dealer_name,
                        d.mobile_number,
                        d.gst_number,
                        d.address,
                        d.status,
                      ]
                    )
                  }
                  className="btn btn-secondary d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFilePdf size={14} />
                  PDF
                </button>
              </>
            )}
            {selectedCategory === "customer" && (
              <>
                <button
                  onClick={() =>
                    exportToExcel(filteredCustomers, "customer_report.xlsx")
                  }
                  className="btn btn-success d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFileExcel size={14} />
                  Excel
                </button>
                <CSVLink
                  data={filteredCustomers}
                  filename="customer_report.csv"
                  className="btn btn-warning d-flex align-items-center gap-2"
                >
                  <FaFilePdf size={14} />
                  CSV
                </CSVLink>
                <button
                  onClick={() =>
                    exportToPDF(
                      filteredCustomers,
                      "Customer Report",
                      [
                        "Customer ID",
                        "Name",
                        "Customer Type",
                        "Branch Name",
                        "Mobile Number",
                        "Address",
                        "Status",
                      ],
                      (c) => [
                        c.customer_id,
                        c.name,
                        c.customer_type,
                        c.branch_name,
                        c.mobile_number,
                        c.address,
                        c.status,
                      ]
                    )
                  }
                  className="btn btn-secondary d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFilePdf size={14} />
                  PDF
                </button>
              </>
            )}
            {selectedCategory === "profit_report" && (
              <>
                <button
                  onClick={() =>
                    exportToExcel(filteredProfitReport, "profit_report.xlsx")
                  }
                  className="btn btn-success d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFileExcel size={14} />
                  Excel
                </button>
                <CSVLink
                  data={filteredProfitReport}
                  filename="profit_report.csv"
                  className="btn btn-warning d-flex align-items-center gap-2"
                >
                  <FaFilePdf size={14} />
                  CSV
                </CSVLink>
                <button
                  onClick={() =>
                    exportToPDF(
                      filteredProfitReport,
                      "Profit Report",
                      [
                        "Product Category",
                        "Type",
                        "Size",
                        "Purchase Rate",
                        "Mrp",
                        "Profit Value",
                        "Percentage",
                      ],
                      (p) => [
                        p.product_category,
                        p.type,
                        p.size,
                        p.purchase_rate,
                        p.mrp,
                        p.profit_value,
                        p.percentage,
                      ]
                    )
                  }
                  className="btn btn-secondary d-flex align-items-center gap-2"
                  disabled={isLoading}
                >
                  <FaFilePdf size={14} />
                  PDF
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Date Filter Row */}
      <div
        className="card mx-3 mx-md-4 border mb-3"
        style={{ backgroundColor: "#fff0f5" }}
      >
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label small fw-medium">
                Select Category
              </label>
              <select
                className="form-select form-select-sm"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="" disabled>
                  -- Select --
                </option>
                <option value="sales">Sales</option>
                <option value="purchase">Purchase</option>
                <option value="customer">Customer</option>
                <option value="dealer">Dealer</option>
                {/* <option value="stocks">Stocks</option> */}
                <option value="profit_report">Profit Report</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-medium">From Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={
                  selectedCategory === "dealer" ||
                  selectedCategory === "customer"
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-medium">To Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={
                  selectedCategory === "dealer" ||
                  selectedCategory === "customer"
                }
              />
            </div>
            <div className="col-md-3 d-flex align-items-end gap-2">
              <button
                className="btn btn-sm btn-secondary w-50"
                onClick={handleReset}
              >
                Reset
              </button>
              <button
                className="btn btn-sm btn-danger w-50"
                onClick={handleFilter}
              >
                Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card Container */}
      <div className="card mx-3 mx-md-4 border mb-5">
        <div className="card-header bg-white d-flex justify-content-between align-items-center p-3 border-bottom">
          <div
            className="d-flex align-items-center gap-2"
            style={{ height: "36px", width: "120px" }}
          >
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ height: "28px", width: "14px" }}
            >
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                <rect x="1" y="1" width="3" height="3" fill="#666" />
                <rect x="1" y="5" width="3" height="3" fill="#666" />
                <rect x="1" y="9" width="3" height="2" fill="#666" />
                <rect x="5" y="1" width="6" height="1" fill="#666" />
                <rect x="5" y="3" width="6" height="1" fill="#666" />
                <rect x="5" y="5" width="6" height="1" fill="#666" />
                <rect x="5" y="7" width="6" height="1" fill="#666" />
                <rect x="5" y="9" width="6" height="1" fill="#666" />
              </svg>
            </div>
            <select
              className="border-0 bg-transparent text-dark form-select form-select-sm"
              style={{
                boxShadow: "none",
                outline: "none",
                lineHeight: "1.1",
                height: "28px",
                cursor: "pointer",
                width: "80px",
                paddingTop: "0",
                paddingBottom: "0",
              }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="5">Show 5</option>
              <option value="10">Show 10</option>
              <option value="25">Show 25</option>
              <option value={dataToShow.length}>Show All</option>
            </select>
          </div>
          <div className="position-relative" style={{ width: "280px" }}>
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-100 ps-3 pe-5 py-2 border rounded form-control"
              disabled={isLoading}
            />
            <Search
              size={18}
              className="position-absolute top-50 end-0 translate-middle-y me-3 text-danger"
              style={{ pointerEvents: "none" }}
            />
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table
              className="table table-hover mb-0 text-center"
              id="report-table"
            >
              <thead>
                {selectedCategory === "sales" ? (
                  <tr>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 fw-semibold small"
                    >
                      S.No
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 fw-semibold small"
                    >
                      Invoice No
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 fw-semibold small"
                    >
                      Purchase Date
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 fw-semibold small"
                    >
                      Total Amount
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 fw-semibold small"
                    >
                      Paid Amount
                    </th>
                  </tr>
                ) : selectedCategory === "purchase" ? (
                  <tr>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      S.No
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Purchase No
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Date
                    </th>
                  
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Total Amount
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Paid Amount
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Quantity
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Payment Status
                    </th>
                  </tr>
                ) : selectedCategory === "stocks" ? (
                  <tr>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      S.No
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Item Code
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Item Name
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Brand Name
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Model Name
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Type
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Tyre Size
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Stock Qty
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Available Qty
                    </th>
                  </tr>
                ) : selectedCategory === "dealer" ? (
                  <tr>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      S.No
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Dealer ID
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Company Name
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Mobile Number
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      GST Number
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom text-center"
                    >
                      Address
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Status
                    </th>
                  </tr>
                ) : selectedCategory === "customer" ? (
                  <tr>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      S.No
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Customer ID
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Name
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Customer Type
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Branch Name
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Mobile Number
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom text-center"
                    >
                      Address
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Status
                    </th>
                  </tr>
                ) : selectedCategory === "profit_report" ? (
                  <tr>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      S.No
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Product Category
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Type
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Size
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Purchase Rate
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Mrp
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Profit Value
                    </th>
                    <th
                      style={{ backgroundColor: "#d3d3d3" }}
                      className="p-3 text-start fw-semibold small border-bottom"
                    >
                      Percentage
                    </th>
                  </tr>
                ) : (
                  <tr>
                    <th
                      colSpan={9}
                      className="p-3 text-center fw-semibold small"
                    >
                      Please select a category
                    </th>
                  </tr>
                )}
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  selectedCategory === "sales" ? (
                    currentData.map((sale, index) => (
                      <tr key={index} style={{ fontSize: "14px" }}>
                        <td>{startIndex + index + 1}</td>
                        <td>{sale.Sales_ID}</td>
                        <td>{sale.Purchase_Date}</td>
                        <td>{formatCurrency(sale.Net_total)}</td>
                        <td>{formatCurrency(sale.Paid_Amount)}</td>
                      </tr>
                    ))
                  ) : selectedCategory === "purchase" ? (
                    currentData.map((purchase, index) => (
                      <tr key={index} className="border-bottom">
                        <td className="p-3 small">{startIndex + index + 1}</td>
                        <td className="p-3 small fw-medium">
                          {purchase.purchaseId}
                        </td>
                        <td className="p-3 small">{purchase.purchaseDate}</td>

                      
                        <td className="p-3 small">
                          {formatCurrency(purchase.netTotal)}
                        </td>
                        <td className="p-3 small">
                          {formatCurrency(purchase.paidAmount)}
                        </td>
                        <td className="p-3 small">
                          {purchase.purchaseItems?.[0]?.purchase_quantity ||
                            "0"}
                        </td>
                        <td className="p-3 small">
                          {getPaymentStatusBadge(purchase.paymentStatus)}
                        </td>
                      </tr>
                    ))
                  ) : selectedCategory === "stocks" ? (
                    currentData.map((stock, index) => (
                      <tr key={index} className="border-bottom">
                        <td className="p-3 small">{startIndex + index + 1}</td>
                        <td className="p-3 small fw-medium">
                          {stock.item_code || "-"}
                        </td>
                        <td className="p-3 small">{stock.item_name || "-"}</td>
                        <td className="p-3 small">{stock.brand_name || "-"}</td>
                        <td className="p-3 small">{stock.model_name || "-"}</td>
                        <td className="p-3 small">{stock.type || "-"}</td>
                        <td className="p-3 small">{stock.tyre_size || "-"}</td>
                        <td className="p-3 small">
                          {stock.stock_quantity || "0"}
                        </td>
                        <td className="p-3 small">
                          {stock.available_quantity || "0"}
                        </td>
                      </tr>
                    ))
                  ) : selectedCategory === "dealer" ? (
                    currentData.map((dealer, index) => (
                      <tr key={index} className="border-bottom">
                        <td className="p-3 small">{startIndex + index + 1}</td>
                        <td className="p-3 small fw-medium">
                          {dealer.dealer_id}
                        </td>
                        <td className="p-3 small">{dealer.company_name}</td>
                        <td className="p-3 small">{dealer.mobile_number}</td>
                        <td className="p-3 small">{dealer.gst_number}</td>
                        <td className="p-3 small">{dealer.address}</td>
                        <td className="p-3 small">
                          {getPaymentStatusBadge(dealer.status)}
                        </td>
                      </tr>
                    ))
                  ) : selectedCategory === "customer" ? (
                    currentData.map((customer, index) => (
                      <tr key={index} className="border-bottom">
                        <td className="p-3 small">{startIndex + index + 1}</td>
                        <td className="p-3 small fw-medium">
                          {customer.customer_id}
                        </td>
                        <td className="p-3 small">{customer.name}</td>
                        <td className="p-3 small">{customer.customer_type}</td>
                        <td className="p-3 small">{customer.branch_name}</td>
                        <td className="p-3 small">{customer.mobile_number}</td>
                        <td className="p-3 small">{customer.address}</td>
                        <td className="p-3 small">
                          {getPaymentStatusBadge(customer.status)}
                        </td>
                      </tr>
                    ))
                  ) : selectedCategory === "profit_report" ? (
                    currentData.map((profit, index) => (
                      <tr key={index} className="border-bottom">
                        <td className="p-3 small">{startIndex + index + 1}</td>
                        <td className="p-3 small">{profit.product_category}</td>
                        <td className="p-3 small">{profit.type}</td>
                        <td className="p-3 small">{profit.size}</td>
                        <td className="p-3 small">{profit.purchase_rate}</td>
                        <td className="p-3 small">{profit.mrp}</td>
                        <td className="p-3 small">{profit.profit_value}</td>
                        <td className="p-3 small">{profit.percentage}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="p-5 text-center text-muted">
                        {isLoading
                          ? "Loading data..."
                          : error || "No data found"}
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td
                      colSpan={
                        selectedCategory === "sales"
                          ? 7
                          : selectedCategory === "customer"
                          ? 8
                          : selectedCategory === "profit_report"
                          ? 8
                          : 9
                      }
                      className="p-5 text-center text-muted"
                    >
                      {isLoading ? "Loading data..." : error || "No data found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {itemsPerPage < dataToShow.length && (
            <div className="card-footer bg-white d-flex justify-content-between align-items-center border-top">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || isLoading}
                className={`btn btn-sm d-flex align-items-center gap-2 ${
                  currentPage === 1
                    ? "bg-light text-muted"
                    : "bg-white text-dark"
                }`}
              >
                <FaChevronLeft size={14} />
                <span className="ms-1 d-none d-md-inline">Previous</span>
              </button>
              <div className="d-flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      disabled={isLoading}
                      className={`btn btn-sm ${
                        pageNum === currentPage
                          ? "bg-danger bg-opacity-10 text-danger"
                          : "bg-white text-dark"
                      }`}
                      style={{ width: "32px", height: "32px" }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <span className="px-2 py-1">...</span>
                )}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={isLoading}
                    className={`btn btn-sm ${
                      totalPages === currentPage
                        ? "bg-danger bg-opacity-10 text-danger"
                        : "bg-white text-dark"
                    }`}
                    style={{ width: "32px", height: "32px" }}
                  >
                    {totalPages}
                  </button>
                )}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages || isLoading}
                className={`btn btn-sm d-flex align-items-center gap-2 ${
                  currentPage === totalPages
                    ? "bg-light text-muted"
                    : "bg-white text-dark"
                }`}
              >
                <span className="ms-1 d-none d-md-inline">Next</span>
                <FaChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Report;
